import { useState, useEffect } from "react";
import Landing from "./Landing";
import Dashboard from "./Dashboard";
import api from "./api";

/*
 ╔══════════════════════════════════════════════════════════╗
 ║  CHOPTIME AI — App.js                                   ║
 ║  Wakes Render backend first, then shows Landing/Dashboard║
 ║  Every user has their own account — no shared names     ║
 ║                                                          ║
 ║  In memory of Mina Gbemisola LAAK · MGL2390             ║
 ╚══════════════════════════════════════════════════════════╝
*/

const BACKEND = "https://choptime-ai-backend.onrender.com";

export default function App() {
  // "waking" → "landing" → "app"
  const [view, setView]       = useState("waking");
  const [wakeMsg, setWakeMsg] = useState("Starting ChopTime AI…");
  const [wakeSub, setWakeSub] = useState("This takes up to 30 seconds on first load");
  const [dots, setDots]       = useState(0);

  useEffect(() => {
    // Animated dots so user knows something is happening
    const dotTimer = setInterval(() => setDots(d => (d + 1) % 4), 500);

    // Step 1 — wake the Render backend
    setWakeMsg("🔄 Waking up server");
    setWakeSub("Render free tier sleeps after 15 min — please wait…");

    fetch(`${BACKEND}/`, { signal: AbortSignal.timeout(60000) })
      .then(() => {
        clearInterval(dotTimer);
        setWakeMsg("✅ Server is awake!");
        setWakeSub("Loading ChopTime AI…");

        // Step 2 — check if user is already logged in
        setTimeout(() => {
          if (api.auth.isLoggedIn()) {
            // Verify the token is still valid
            api.auth.getProfile()
              .then(() => setView("app"))
              .catch(() => {
                // Token expired — go to landing
                api.auth.logout();
                setView("landing");
              });
          } else {
            setView("landing");
          }
        }, 500);
      })
      .catch(() => {
        clearInterval(dotTimer);
        // Backend didn't respond in time — still proceed
        // Individual API calls will fail gracefully with their own error handling
        setWakeMsg("⚠️ Server slow to respond");
        setWakeSub("Loading anyway — some features may be limited");
        setTimeout(() => {
          if (api.auth.isLoggedIn()) {
            setView("app");
          } else {
            setView("landing");
          }
        }, 1500);
      });

    return () => clearInterval(dotTimer);
  }, []);

  function enterApp()  { setView("app"); }
  function goToLanding() {
    api.auth.logout();
    setView("landing");
  }

  // ── WAKE SCREEN ──────────────────────────────────────────
  if (view === "waking") {
    const dotStr = ".".repeat(dots);
    return (
      <div style={{
        minHeight: "100vh",
        background: "#06090b",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 20,
        fontFamily: "'DM Sans', sans-serif",
        padding: 24,
      }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Syne:wght@800;900&family=DM+Sans:wght@400;500&display=swap');
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes pulse { 0%,100%{opacity:.4} 50%{opacity:1} }
        `}</style>

        {/* Animated ring */}
        <div style={{ position: "relative", width: 80, height: 80 }}>
          <svg viewBox="0 0 80 80" style={{ position: "absolute", inset: 0, animation: "spin 1.2s linear infinite" }}>
            <circle cx="40" cy="40" r="34" fill="none" stroke="#192830" strokeWidth="6" />
            <circle cx="40" cy="40" r="34" fill="none" stroke="#00d4a0" strokeWidth="6"
              strokeLinecap="round" strokeDasharray="213" strokeDashoffset="160" />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>
            🌿
          </div>
        </div>

        {/* Logo */}
        <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 900, color: "#00d4a0" }}>
          ChopTime AI
        </div>
        <div style={{ fontSize: 12, color: "#6e9e8c", marginTop: -12 }}>
          West Africa's Diabetes Voice
        </div>

        {/* Status */}
        <div style={{
          background: "#0c1419",
          border: "1px solid #192830",
          borderRadius: 12,
          padding: "14px 24px",
          textAlign: "center",
          maxWidth: 320,
        }}>
          <div style={{ fontSize: 14, color: "#deeee8", fontWeight: 500, marginBottom: 4 }}>
            {wakeMsg}{dotStr}
          </div>
          <div style={{ fontSize: 12, color: "#6e9e8c", lineHeight: 1.6 }}>
            {wakeSub}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ width: 200, height: 3, background: "#192830", borderRadius: 2, overflow: "hidden" }}>
          <div style={{
            height: "100%", background: "#00d4a0", borderRadius: 2,
            animation: "pulse 1.5s ease-in-out infinite",
            width: "60%",
          }} />
        </div>

        <div style={{ fontSize: 11, color: "#2e4e44", marginTop: 8 }}>
          🕯️ MGL2390 · In memory of Mina Gbemisola LAAK
        </div>
      </div>
    );
  }

  // ── DASHBOARD ────────────────────────────────────────────
  if (view === "app") {
    return <Dashboard onLogout={goToLanding} />;
  }

  // ── LANDING (default) ────────────────────────────────────
  return <Landing onEnterApp={enterApp} />;
}
